import { Alert, Badge, Box, Button, Card, Center, Group, Loader, Modal, Stack, Text, TextInput, ThemeIcon, Title, type BadgeProps, type CardProps, type TextInputProps } from '@mantine/core';
import { IconAlertTriangle, IconInbox, IconSearch } from '@tabler/icons-react';
import { forwardRef, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import { tr } from '../lib/i18n';

/** A white panel. `title` + `actions` make a header row; `flush` drops the padding (tables). */
export function Panel({ title, description, actions, flush, children, ...rest }: { title?: ReactNode; description?: ReactNode; actions?: ReactNode; flush?: boolean; children?: ReactNode } & Omit<CardProps, 'title' | 'children'>) {
  return (
    <Card padding={0} {...rest}>
      {(title || actions) && (
        <Group justify="space-between" align="center" wrap="wrap" gap="sm" px="lg" py="md" style={{ borderBottom: flush ? '1px solid var(--pf-line)' : undefined }}>
          <Box style={{ minWidth: 0 }}>
            {title && <Title order={3} fz="md">{title}</Title>}
            {description && <Text size="sm" c="dimmed" mt={2}>{description}</Text>}
          </Box>
          {actions && <Group gap="xs" wrap="wrap">{actions}</Group>}
        </Group>
      )}
      <Box p={flush ? 0 : 'lg'} pt={!flush && (title || actions) ? 0 : undefined}>{children}</Box>
    </Card>
  );
}

/** The filter row above a table: search + selects on one side, actions on the other. */
export function Toolbar({ children, end }: { children?: ReactNode; end?: ReactNode }) {
  return (
    <Group justify="space-between" align="center" wrap="wrap" gap="sm" px="md" py="sm" style={{ borderBottom: '1px solid var(--pf-line)' }}>
      <Group gap="xs" wrap="wrap" style={{ flex: 1, minWidth: 0 }}>{children}</Group>
      {end && <Group gap="xs" wrap="wrap">{end}</Group>}
    </Group>
  );
}

export const SearchInput = forwardRef<HTMLInputElement, TextInputProps>(function SearchInput(props, ref) {
  return <TextInput ref={ref} leftSection={<IconSearch size={16} stroke={1.7} />} placeholder={tr('بحث…', 'Search…')} w={{ base: '100%', sm: 260 }} {...props} />;
});

export type Tone = 'green' | 'yellow' | 'red' | 'blue' | 'gray' | 'orange' | 'violet' | 'teal';
/** Status as a small tinted label with a dot. */
export function StatusBadge({ tone = 'gray', children, ...rest }: { tone?: Tone; children: ReactNode } & Omit<BadgeProps, 'color' | 'children'>) {
  return (
    <Badge color={tone} variant="light" leftSection={<Box w={6} h={6} style={{ borderRadius: 99, background: 'currentColor' }} />} {...rest}>
      {children}
    </Badge>
  );
}

export function Code({ children }: { children: ReactNode }) {
  return <span className="pf-code">{children}</span>;
}

/** Arabic name with the English one under it (or a «missing English» hint). */
export function BilingualText({ ar, en, size = 'sm' }: { ar: string; en?: string | null; size?: 'xs' | 'sm' | 'md' }) {
  return (
    <Box style={{ minWidth: 0 }}>
      <Text size={size} fw={500} lh={1.35}>{ar}</Text>
      {en?.trim() ? (
        <Text size="xs" c="dimmed" lh={1.35} dir="ltr" ta="start">{en}</Text>
      ) : (
        <Text size="xs" c="orange.7" lh={1.35}>{tr('بدون إنجليزي', 'No English')}</Text>
      )}
    </Box>
  );
}

export function Loading({ h = 220 }: { h?: number }) {
  return (
    <Center h={h}>
      <Loader size="sm" color="ink" type="dots" />
    </Center>
  );
}

export function errorText(error: unknown) {
  return error instanceof ApiError ? error.message : error instanceof Error ? error.message : tr('تعذر تحميل البيانات', 'Unable to load data');
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <Alert color="red" variant="light" radius="md" icon={<IconAlertTriangle size={18} />} title={tr('تعذر التحميل', 'Could not load')}>
      <Group justify="space-between" gap="sm">
        <Text size="sm">{errorText(error)}</Text>
        {onRetry && <Button size="xs" variant="white" color="red" onClick={onRetry}>{tr('إعادة المحاولة', 'Retry')}</Button>}
      </Group>
    </Alert>
  );
}

export function EmptyState({ title, text, icon, action, h }: { title: string; text?: string; icon?: ReactNode; action?: ReactNode; h?: number }) {
  return (
    <Center h={h} py={h ? undefined : 48} px="md">
      <Stack align="center" gap={8} maw={360} ta="center">
        <ThemeIcon size={44} radius="xl" variant="light" color="gray">{icon ?? <IconInbox size={22} stroke={1.6} />}</ThemeIcon>
        <Text fw={600} size="sm">{title}</Text>
        {text && <Text size="sm" c="dimmed">{text}</Text>}
        {action}
      </Stack>
    </Center>
  );
}

/** A headline number with a label and an optional hint under it. */
export function Kpi({ label, value, hint, tone, icon }: { label: ReactNode; value: ReactNode; hint?: ReactNode; tone?: Tone; icon?: ReactNode }) {
  return (
    <Card padding="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
        <Text size="sm" c="dimmed" fw={500}>{label}</Text>
        {icon && <ThemeIcon variant="light" color={tone ?? 'gray'} size={28} radius="md">{icon}</ThemeIcon>}
      </Group>
      <Text className="pf-num" fz={26} fw={600} lh={1.2} mt={6}>{value}</Text>
      {hint && <Text size="xs" c="dimmed" mt={4}>{hint}</Text>}
    </Card>
  );
}

/** Confirm before an action (same props as the old dialog). */
export function ConfirmModal(p: { open: boolean; title: string; text: ReactNode; confirmLabel?: string; danger?: boolean; busy?: boolean; disabled?: boolean; onConfirm: () => void; onClose: () => void; children?: ReactNode }) {
  return (
    <Modal opened={p.open} onClose={p.onClose} title={p.title} size="sm">
      <Stack gap="md">
        <Text size="sm" c="dimmed" component="div">{p.text}</Text>
        {p.children}
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={p.onClose}>{tr('إلغاء', 'Cancel')}</Button>
          <Button color={p.danger ? 'red' : 'ink'} onClick={p.onConfirm} loading={p.busy} disabled={p.disabled}>{p.confirmLabel ?? tr('تأكيد', 'Confirm')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Label + value in a details list. */
export function Field({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md" py={9} style={{ borderBottom: '1px solid var(--pf-line)' }}>
      <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>{label}</Text>
      <Box ta="end" style={{ minWidth: 0, fontSize: 'var(--mantine-font-size-sm)' }}>{children}</Box>
    </Group>
  );
}

/** The old label maps speak MUI colors (success / warning / error / default); this is their tone here. */
export const toneOf = (c: string | undefined): Tone => (c === 'success' ? 'green' : c === 'warning' ? 'orange' : c === 'error' ? 'red' : c === 'info' ? 'blue' : 'gray');
